import { z } from 'zod/v4';
import type { Account } from '@/modules/accounting/account.type';

const emojiRegex =
  /([\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2300}-\u{23FF}\u{2B50}\u{1F004}\u{1F0CF}\u{1F18E}\u{3299}\u{3297}\u{303D}\u{00A9}\u{00AE}\u{2122}\u{23F3}\u{24C2}\u{25AA}\u{25AB}\u{25FE}\u{25FD}\u{25FB}\u{25FC}\u{2B1B}\u{2B1C}\u{25B6}\u{25C0}\u{1F200}-\u{1F251}])/u;

/** Includes the dot and lower-cased, or '' when the name has no extension.
 * Lives here rather than in `fileUtils` so isomorphic modules and server
 * actions can use it without pulling `@/icons/*` into their graph. */
export const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
};

export const withoutFirstEmoji = (str: string): string => {
  if (str) {
    return str.replace(emojiRegex, '');
  }

  return str;
};
/* AUTO ADDED DELETE ME */
/* eslint-disable-next-line consistent-return */
export const getFirstEmoji = (str: string): string | void => {
  if (str) {
    const matches = str.match(emojiRegex);

    if (matches) {
      return matches[0];
    }
  }
};

const removeCompanyTypes = (name: string) => {
  const companyTypesRegex =
    /\b(LLC|Inc|Corp|Ltd|GmbH|AG|S\.A\.|NV|LLP|LLLP)\b/gi;
  return name.replace(companyTypesRegex, '');
};

export const nameToInitials = (name: string): string => {
  // Remove company types and non-alphanumeric characters, trim, and convert to uppercase
  const cleanedName = removeCompanyTypes(name.replace(/[^a-zA-Z0-9 ]/g, ''))
    .trim()
    .toUpperCase();

  // Split the cleaned name into parts
  const nameParts = cleanedName.split(' ');

  if (nameParts.length === 0) {
    return '';
  }

  // If there's only one part, return the first character of the first part
  if (nameParts.length < 2) {
    return nameParts[0][0] || '';
  }

  // Return the first character of the first part and the first character of the last part
  return `${nameParts[0][0] || ''}${nameParts[nameParts.length - 1][0] || ''}`;
};

export const accountNameAndNumber = (account?: Partial<Account>): string =>
  account
    ? `${account.account_number ? `${account.account_number} ` : ''}${
        account.name
      }`
    : '';

// `guid`, not `uuid`: Zod 4's `uuid()` also enforces the RFC 9562 version and
// variant nibbles, which rejects ids the old check accepted (the all-zero
// sentinel among them). Module scope: the check runs per path segment.
const guidSchema = z.guid();

export const checkIfValidUUID = (str?: string): boolean =>
  guidSchema.safeParse(str).success;

export const checkIfStringsDifferOnlyByUUID = (
  string1: string,
  string2: string,
): boolean => {
  const parts1 = string1.split('/');
  const parts2 = string2.split('/');

  // If the string have different lengths, they don't match
  if (parts1.length !== parts2.length) {
    return false;
  }

  // Compare each segment
  for (let i = 0; i < parts1.length; i++) {
    const isUUID1 = checkIfValidUUID(parts1[i]);
    const isUUID2 = checkIfValidUUID(parts2[i]);

    // If one is a UUID but the other isn't, the paths don't match
    if (isUUID1 !== isUUID2) {
      return false;
    }

    // If neither are UUIDs, they must match exactly
    if (!isUUID1 && parts1[i] !== parts2[i]) {
      return false;
    }
  }

  return true;
};

export const removeSpecialCharactersExpectSpace = (str: string): string =>
  str.replace(/[^a-zA-Z0-9 ]/g, '');

export const bytesToMB = (bytes: number): string =>
  `${(bytes / 1024 / 1024).toFixed(3)}MB`;

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function capitalizeEveryWord(string: string): string {
  return string
    .split(' ')
    .map((word) => {
      if (!word) return word;

      // Find the first letter character
      const firstLetterIndex = word.search(/[a-zA-Z]/);

      if (firstLetterIndex === -1) {
        // No letters found, return as is
        return word;
      }

      // Capitalize the first letter and lowercase the rest of the letters
      const beforeFirstLetter = word.slice(0, firstLetterIndex);
      const firstLetter = word[firstLetterIndex].toUpperCase();
      const afterFirstLetter = word.slice(firstLetterIndex + 1).toLowerCase();

      return beforeFirstLetter + firstLetter + afterFirstLetter;
    })
    .join(' ');
}

export function replaceUnderscoreWithSpace(value: string): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/_/g, ' ');
}

export function isNumberChar(value: string = ''): boolean {
  return (
    value.length > 0 &&
    (!Number.isNaN(Number(value)) || value === '.') &&
    value !== ' '
  );
}

export const isNumberString = (value: string): boolean =>
  !Number.isNaN(Number(value));

export function splitString(str: string, index: number): [string, string] {
  return [str.slice(0, index), str.slice(index)];
}
